// Facturación a Público en General — one row per CFDI stamped to the generic
// RFC (XAXX010101000), grouped by the real client behind it.
//
// Scope notes:
//  - "Público en General" is detected on the invoice, not on the client record:
//    invoicing.js forces Alegra contact id "1" whenever the RFC is XAXX010101000.
//    customers.rfc is unreliable here — plenty of clients carry XAXX as a
//    placeholder while their CFDI actually went out to a real company RFC.
//  - Cancelled CFDIs (status = 'void') are excluded.
//  - The period filters by invoice issue date (invoices_issued.date).
//  - Notas de crédito on these invoices are negative rows ("NC 7 · GOMV5100")
//    in the period of their own date, so totals are net.
//
// The table starts collapsed: one line per client with its totals, expandable
// to the individual invoices (see `collapseGroups` in Report.js).
import supabase from "../../../supabase-client";

const fmtMoney = (v) => "$" + Math.round(Number(v) || 0).toLocaleString("es-MX");
const fmtDate = (v) => {
  if (!v) return "";
  const [y, m, d] = String(v).substring(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export const publicoGeneralInvoicingReport = {
  id: "publico-general-invoicing",
  title: "Facturación a Público en General",
  description:
    "CFDIs emitidos al RFC genérico (XAXX010101000) en el periodo, agrupados por cliente y desplegables factura por factura. Las notas de crédito restan.",
  category: "Finanzas",
  collapseGroups: true,
  defaultSort: { key: "client_name", direction: "ascending" },
  filters: [
    { type: "daterange", fromKey: "from", toKey: "to", label: "Periodo", required: true },
  ],
  summary: [
    { type: "count", label: "Facturas" },
    { type: "sum", label: "Sin IVA", valueKey: "subtotal", valueFormat: fmtMoney },
    { type: "sum", label: "IVA", valueKey: "tax", valueFormat: fmtMoney },
    { type: "sum", label: "Total Facturado", valueKey: "total", valueFormat: fmtMoney },
    {
      type: "groupBy",
      label: "Por Asesor",
      key: "vendor",
      valueKey: "total",
      valueFormat: fmtMoney,
      format: (v) => v ?? "Sin asesor",
    },
    {
      type: "groupBy",
      label: "Por Sucursal",
      key: "branch",
      valueKey: "total",
      valueFormat: fmtMoney,
      format: (v) => v ?? "Sin sucursal",
    },
  ],
  columns: [
    // groupable: with the default sort (Cliente asc) each client becomes a
    // collapsible subheader carrying its invoice count and money subtotals.
    {
      key: "client_name",
      label: "Cliente",
      width: 260,
      truncate: true,
      groupable: true,
      groupNoun: "factura",
    },
    {
      key: "client_type",
      label: "Tipo",
      format: (v) => (v === "company" ? "Empresa" : "Persona"),
    },
    { key: "invoice_number", label: "Factura" },
    { key: "invoice_date", label: "Fecha", format: fmtDate, exportType: "date", exportNumFmt: "dd/mm/yyyy" },
    { key: "reservations", label: "Reservas", width: 140, truncate: true },
    { key: "branch", label: "Sucursal", width: 180, truncate: true },
    { key: "segment", label: "Segmento" },
    { key: "vendor", label: "Asesor" },
    { key: "subtotal", label: "Sin IVA", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "tax", label: "IVA", format: fmtMoney, subtotal: true, exportType: "currency" },
    { key: "total", label: "Total", format: fmtMoney, subtotal: true, exportType: "currency" },
  ],
  async fetch({ from, to }) {
    if (!from || !to) throw new Error("Seleccionar periodo");
    const { data, error } = await supabase.rpc("report_publico_general_invoicing", {
      p_from: from,
      p_to: to,
    });
    if (error) throw error;
    return data || [];
  },
};
